import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import {
  PageHeader,
  Card,
  Stat,
  Badge,
  Spinner,
  EmptyState,
  SearchInput,
  Table,
  type TableColumn,
} from "../ds";
import { CertWarning } from "../components/CertWarning";
import { api, errorText, type Device } from "../lib/api";
import { useFleetEvents } from "../lib/hooks/useFleetEvents";
import { fmtDateTime } from "../lib/format";

// Spaltensatz der Flotten-Tabelle. `hideOnMobile` = auf Telefon-Breite raus:
// Status, Hostname und „zuletzt gesehen“ beantworten dort die einzige Frage
// („lebt der Router?“), der Rest steht auf der Detailseite.
const COLUMNS: TableColumn<Device>[] = [
  {
    key: "status",
    header: "Status",
    cell: (d) => (
      <Badge variant={d.online ? "success" : "neutral"} dot>
        {d.online ? "online" : "offline"}
      </Badge>
    ),
  },
  {
    key: "hostname",
    header: "Hostname",
    cell: (d) => (
      <Link to={`/fleet/${d.id}`} className="ds-link">
        {d.hostname ?? d.id}
      </Link>
    ),
  },
  { key: "model", header: "Modell", hideOnMobile: true, cell: (d) => d.model ?? "—" },
  {
    key: "firmware",
    header: "Firmware",
    hideOnMobile: true,
    // Version UND Bau-Stempel: zwischen zwei Builds derselben OpenWrt-Version
    // liegen Agent- und Paketstände, die Version allein sagt also nichts
    // darüber, was hier wirklich läuft.
    cell: (d) => (
      <span className="flex flex-col">
        <span>{d.firmwareVersion ?? "—"}</span>
        {/* Gleiche Textfarbe wie die Version: als gedimmte Zweitzeile war der
            Stempel auf dem dunklen Hintergrund nicht mehr zu lesen. */}
        {d.imageBuild && <span className="font-mono text-xs">{d.imageBuild}</span>}
      </span>
    ),
  },
  { key: "wgip", header: "WG-IP", mono: true, hideOnMobile: true, cell: (d) => d.wgIp ?? "—" },
  { key: "lastSeen", header: "Zuletzt gesehen", cell: (d) => fmtDateTime(d.lastSeen) },
];

// Fleet-Übersicht (P1 „Sichtbarkeit“): alle Router, online/offline, last-seen,
// Firmware. Datenquelle = Spyship-Binary /api/devices; live-Status kommt aus der
// Connection-Registry (welcher Router gerade am mTLS-WS hängt).
export function FleetPage() {
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  // Einmaliger Seed der Liste (Online-Registry) — kostet kein Router-Volumen.
  // Der Zustand hängt am Ergebnis, nicht am Absenden: ein `setError(null)` vorab
  // wäre beim Aufruf aus dem Effekt ein Zustandswechsel im Renderlauf.
  const load = useCallback(() => {
    api
      .listDevices()
      .then((d) => {
        setDevices(d);
        setError(null);
      })
      .catch((e: unknown) => setError(errorText(e)));
  }, []);

  useEffect(load, [load]); // Seed; danach live über den Fleet-Stream, kein Poll

  // Push aus der Registry: eine geänderte Zeile ersetzt die alte, ein frisch
  // verbundener Router kommt hinten dran (Sortierung zieht der nächste Seed nach).
  const onDevice = useCallback((row: Device) => {
    setDevices((prev) => {
      if (!prev) return prev; // Seed noch nicht da — er bringt die Zeile mit
      const idx = prev.findIndex((d) => d.id === row.id);
      if (idx === -1) return [...prev, row];
      const next = prev.slice();
      next[idx] = row;
      return next;
    });
  }, []);
  useFleetEvents({ onDevice });

  const online = devices?.filter((d) => d.online).length ?? 0;
  const total = devices?.length ?? 0;

  // Gesucht wird über das, was in der Tabelle steht — inklusive der Spalten,
  // die auf dem Telefon ausgeblendet sind: wer nach einer WG-IP sucht, will sie
  // finden, auch wenn sie gerade nicht sichtbar ist.
  const zeilen = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q || !devices) return devices ?? [];
    return devices.filter((d) =>
      [d.hostname, d.id, d.model, d.firmwareVersion, d.imageBuild, d.wgIp]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q)),
    );
  }, [devices, query]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Fleet"
        subtitle="Alle Eulektro-Router — Live-Status über den mTLS-WebSocket."
      />

      <CertWarning />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Router gesamt" value={total} iconName="fleet" loading={devices === null} />
        <Stat label="Online" value={online} iconName="activity" loading={devices === null} />
        <Stat label="Offline" value={total - online} iconName="power" loading={devices === null} />
        <Stat
          label="Online-Quote"
          value={total ? Math.round((online / total) * 100) : 0}
          unit="%"
          loading={devices === null}
        />
      </div>

      <Card
        title="Router"
        bodyPadding={false}
        actions={
          devices && devices.length > 0 ? (
            <SearchInput
              value={query}
              onChange={setQuery}
              placeholder="Hostname, Modell, Firmware, WG-IP …"
            />
          ) : undefined
        }
      >
        {error ? (
          <EmptyState iconName="close" title="Fehler" description={error} />
        ) : devices === null ? (
          <div className="flex justify-center p-10">
            <Spinner />
          </div>
        ) : devices.length === 0 ? (
          <EmptyState
            iconName="fleet"
            title="Noch keine Router"
            description="Sobald ein Router mit gültigem Zertifikat einrollt, taucht er hier auf."
          />
        ) : (
          <Table
            columns={COLUMNS}
            rows={zeilen}
            rowKey={(d) => d.id}
            emptyMessage="Kein Router passt zur Suche."
          />
        )}
      </Card>
    </div>
  );
}
